import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput, Th, Td } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function AdminCalendar() {
  const [events, setEvents] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [f, setF] = useState({ start_date: "", end_date: "", reason: "", kind: "blackout" });
  const nav = useNavigate();
  const load = () => { api.get("/admin/calendar").then((r) => setEvents(r.data)).catch(() => setEvents([])); api.get("/admin/blackout").then((r) => setBlocks(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const addBlock = async () => {
    if (!f.start_date || !f.end_date) { toast.error("Choose dates."); return; }
    try { await api.post("/admin/blackout", f); toast.success("Dates blocked."); setF({ start_date: "", end_date: "", reason: "", kind: "blackout" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delBlock = async (id) => { try { await api.delete(`/admin/blackout/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const sorted = (events || []).filter((e) => !e.block).sort((a, b) => (a.start || "").localeCompare(b.start || ""));

  return (
    <div>
      <AdminHeading title="Calendar & Availability" subtitle="Agenda of upcoming stays, events, and blocked periods." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Panel title="Agenda">
            {events === null ? <Loading /> : sorted.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nothing scheduled.</p> : (
              <table className="w-full"><thead><tr><Th>Dates</Th><Th>Reservation</Th><Th>Status</Th></tr></thead>
                <tbody>{sorted.map((e) => (
                  <tr key={e.id} className="cursor-pointer hover:bg-white/5" onClick={() => nav(`/admin/reservations/${e.id}`)}>
                    <Td className="whitespace-nowrap text-muted-foreground">{fmtDate(e.start)} → {fmtDate(e.end)}</Td>
                    <Td>{e.title}</Td><Td><StatusBadge status={e.status} /></Td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </Panel>
        </div>
        <div>
          <Panel title="Blocked Dates" action={
            <Dialog><DialogTrigger asChild><button className="btn-ghost-gold px-3 py-1.5 text-xs flex items-center gap-1" data-testid="block-dates-btn"><Plus size={13} /> Block</button></DialogTrigger>
              <DialogContent className="bg-surface border-white/10">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Block Dates</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <input type="date" className={adminInput} value={f.start_date} onChange={(e) => setF((p) => ({ ...p, start_date: e.target.value }))} />
                  <input type="date" className={adminInput} value={f.end_date} onChange={(e) => setF((p) => ({ ...p, end_date: e.target.value }))} />
                  <select className={adminInput} value={f.kind} onChange={(e) => setF((p) => ({ ...p, kind: e.target.value }))}><option value="blackout">Blackout</option><option value="maintenance">Maintenance</option><option value="personal">Personal use</option></select>
                  <input className={adminInput} placeholder="Reason" value={f.reason} onChange={(e) => setF((p) => ({ ...p, reason: e.target.value }))} />
                  <button onClick={addBlock} className="btn-gold w-full py-2 text-sm" data-testid="block-save">Block Dates</button>
                </div>
              </DialogContent>
            </Dialog>
          }>
            {blocks.length === 0 ? <p className="text-sm text-muted-foreground">No blocked dates.</p> : (
              <div className="space-y-2">{blocks.map((b) => (
                <div key={b.id} className="flex justify-between items-center text-sm">
                  <div><p>{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</p><p className="text-xs text-muted-foreground">{b.kind} · {b.reason}</p></div>
                  <button onClick={() => delBlock(b.id)} className="text-red-300 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              ))}</div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
